<script lang="ts" setup>
import moment from "jalali-moment";
import { PlanType } from "~/types/models/enums";
import type {
  FusionSharedDtosMoodleStudentResponseDto,
  FusionSharedDtosMoodleClassAttendanceResponseDto,
  FusionSharedDtosMoodleStudentLoginLogResponseDto,
  FusionSharedDtosMoodleTutorContactInfoResponseDto,
  FusionSharedDtosMoodleStudentPerformanceResponseDto,
  FusionSharedDtosMoodleUpcomingEventDto,
  FusionSharedDtosMoodleStudentCourseDto, FusionSharedDtosAccountUserDto, FusionSharedDtosMoodleStudentXpDto,
} from "~/types/api/api";
import navigateAfterLogin from "~/utils/functions/navigate-after-login";

definePageMeta({ layout: "default" });

useHead({
  title: "گزارش والدین"
})

const { $api } = useNuxtApp();

const objectId = ref<string>("");
const showTutorRatingModal = ref(false);
const studentId = ref<string>();
const studentList = ref<FusionSharedDtosAccountUserDto[]>();
const courseId = ref<number>();
const studentCourses = ref<FusionSharedDtosMoodleStudentCourseDto[]>([]);
const studentInfo = ref<FusionSharedDtosMoodleStudentResponseDto>();
const studentLoginLog = ref<FusionSharedDtosMoodleStudentLoginLogResponseDto[]>([]);
const statusClassAttendance = ref<FusionSharedDtosMoodleClassAttendanceResponseDto[]>([]);
const studentPerformance = ref<FusionSharedDtosMoodleStudentPerformanceResponseDto[]>([]);
const tutorContactInfo = ref<FusionSharedDtosMoodleTutorContactInfoResponseDto[]>([]);
const upcomingEvents = ref<FusionSharedDtosMoodleUpcomingEventDto[]>([]);
const studentXp = ref<FusionSharedDtosMoodleStudentXpDto>();

const isPremium = computed(() => studentInfo.value?.planType === (PlanType.Premium as number));
const subscriptionPeriod = computed(() => {
  return studentInfo.value?.subscriptionPeriod ?
      `از ${moment(studentInfo.value.subscriptionPeriod.from).locale('fa').format(
          "D MMMM"
      )} تا ${moment(studentInfo.value.subscriptionPeriod.to).locale('fa').format(
          "D MMMM"
      )} ${moment(studentInfo.value.subscriptionPeriod.to).locale('fa').format("YYYY")}`
      : "اشتراک فعال ندارد.";
});

const selectedCourse = () => {
  $api.api.parentReportGetStatusClassAttendanceList({ ChildId: studentId.value, CourseId: courseId.value })
      .then(res => {
        statusClassAttendance.value = res.data;
      });

  $api.api.parentReportGetStudentPerformanceList({ ChildId: studentId.value, CourseId: courseId.value })
      .then(res => {
        studentPerformance.value = res.data;
      });

  $api.api.parentReportGetTutorContactInfoList({ ChildId: studentId.value, CourseId: courseId.value })
      .then(res => {
        tutorContactInfo.value = res.data;
      });

  $api.api.parentReportGetUpcomingEventsList({ ChildId: studentId.value, CourseId: courseId.value })
      .then(res => {
        upcomingEvents.value = res.data;
      });

  $api.api.parentReportGetStudentXpList({ ChildId: studentId.value, CourseId: courseId.value })
      .then(res => {
        studentXp.value = res.data;
      });
};

const selectedStudent = async () => {
  $api.api.npsPendingNpsFormList()
      .then(res => {
        objectId.value = res.data.items?.find(i => i.studentId === studentId.value)?.objectId || "";
      });

  const res = await $api.api.parentReportGetStudentInfoList({ studentId: studentId.value });
  studentInfo.value = res.data;
  studentCourses.value = res.data.courses || [];
  courseId.value = res.data.courses?.at(0)?.id;
  $api.api.parentReportGetStudentLoginLogList({ studentId: studentId.value })
      .then(res => {
        studentLoginLog.value = res.data;
      });
  selectedCourse();
};

onMounted(async () => {
  const response = await $api.api.userGetUserChildrenList();
  studentList.value = response.data;
  studentId.value = response.data.at(0)?.id;
  await selectedStudent();
});
</script>

<template>
  <PerfectScrollbar class="h-lg-screen">
    <div class="banner-section w-full pa-4">
      <VBtn variant="text" prepend-icon="mdi-arrow-right" @click="navigateAfterLogin(4)">بازگشت به داشبورد</VBtn>
      <h3 class="text-no-wrap mt-10 mb-3 text-center">گزارش عملکرد ماهانه</h3>
      <h5 class="text-no-wrap mb-10 text-center" style="font-size: 12px">(نسخه آزمایشی)</h5>
    </div>
    <VContainer class="d-flex">
      <VRow>
        <VCol cols="12" md="5" lg="4" class="sidebar-section">
          <div class="personal-info-section">
            <VImg src="/new/images/svg/avatar-default.svg" class="profile-image"/>
            <VSelect
                v-model="studentId"
                item-title="displayName"
                item-value="id"
                label="فراگیر انتخاب شده:"
                :items="studentList"
                @update:model-value="selectedStudent"/>
            <VSelect
                v-model="courseId"
                item-title="name"
                item-value="id"
                label="درس انتخاب شده:"
                :items="studentCourses"
                @update:model-value="selectedCourse"/>
            <h4 class="text-center mb-8">درس جاری: {{ studentCourses[0]?.name || "" }}</h4>
            <p class="text-center text-light-blue-darken-3 mb-8">
              <strong>تاریخ اشتراک:</strong> {{ subscriptionPeriod }}
            </p>
            <PagesReportPerformanceResultCard
                v-if="isPremium"
                good-text="خوب"
                great-text="عالی"
                perfect-text="منتخب"
                title="عملکرد در دوره"
                emptyText="فرزند شما تاکنون فعالیتی نداشته است."
                :items="studentPerformance"
            />
            <PagesReportStudentXpCard :item="studentXp"/>
            <div v-if="objectId">
              <VBtn block color="primary" class="mb-4" @click="showTutorRatingModal = true">
                نظرسنجی مربی
              </VBtn>
              <p class="tip">
                با انجام نظر‌سنجی مربی به بهبود مستمر کلاس‌ها و خدمات آکادمی یاسان کمک می‌کنید.
              </p>
            </div>
          </div>
        </VCol>
        <VCol cols="12" md="7" lg="8">
          <VRow>
            <VCol cols="12">
              <LazyPagesReportLoginLogCard :loginLogs="studentLoginLog"/>
            </VCol>
            <VCol cols="12" :lg="isPremium ? 6 : 12">
              <LazyPagesReportClassAttendanceCard :isPremium="isPremium" :items="statusClassAttendance"/>
            </VCol>
            <VCol v-if="isPremium" cols="12" lg="6">
              <LazyPagesReportPerformanceCard :items="studentPerformance"/>
            </VCol>
            <VCol cols="12" lg="6">
              <LazyPagesReportTutorContactCard :items="tutorContactInfo"/>
            </VCol>
            <VCol cols="12" lg="6">
              <LazyPagesReportUpcomingEventCard :items="upcomingEvents"/>
            </VCol>
          </VRow>
        </VCol>
      </VRow>
    </VContainer>
  </PerfectScrollbar>
  <LazyPagesReportTutorRatingModal v-model="showTutorRatingModal" :objectId="objectId"/>
</template>
